package org.example.project.dashboard

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import kotlinx.browser.document
import kotlinx.browser.window
import org.example.project.engineletter.RenderYardLetter
import org.example.project.engineletter.renderLetterDocumentPdfFile
import org.example.project.service.ServiceDocumentViewerService
import org.example.project.settings.LumaticaButton
import org.example.project.settings.LumaticaButtonVariant
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.w3c.dom.HTMLAnchorElement
import org.w3c.dom.HTMLElement

private const val SERVICE_DOCUMENT_SNAPSHOT_RENDER_ROOT = "service-document-snapshot-render-root"

@Composable
fun ServiceDocumentViewerOverlayLayer() {
    val state = ServiceDocumentViewerService.current ?: return
    val snapshot = state.snapshot

    LaunchedEffect(snapshot, state.fileName) {
        if (snapshot == null || state.blobUrl.isNotBlank()) return@LaunchedEffect
        val root = document.getElementById(SERVICE_DOCUMENT_SNAPSHOT_RENDER_ROOT) as? HTMLElement
            ?: return@LaunchedEffect
        runCatching {
            val pdfFile = renderLetterDocumentPdfFile(root, state.fileName)
            val pdfUrl = window.asDynamic().URL.createObjectURL(pdfFile) as String
            ServiceDocumentViewerService.setRenderedPdf(pdfUrl)
        }.onFailure { console.error("Yard snapshot PDF rendering failed", state.fileName, it) }
    }

    Div({
        style {
            position(Position.Fixed)
            property("inset", "0")
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            backgroundColor(rgba(0, 0, 0, 0.78))
            property("backdrop-filter", "blur(8px)")
            property("z-index", "130000")
            padding(18.px)
            boxSizing("border-box")
            gap(12.px)
        }
    }) {
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.SpaceBetween)
                gap(12.px)
                minHeight(42.px)
                color(DashboardTheme.TEXT_LIGHT)
            }
        }) {
            Div({
                style {
                    minWidth(0.px)
                    property("white-space", "nowrap")
                    property("overflow", "hidden")
                    property("text-overflow", "ellipsis")
                    fontSize(18.px)
                    fontWeight("700")
                }
            }) { Text(state.title) }
            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(10.px)
                    flexShrink(0)
                }
            }) {
                LumaticaButton(
                    label = "Download",
                    variant = LumaticaButtonVariant.Standard,
                    heightPx = 38,
                    enabled = state.blobUrl.isNotBlank(),
                    onClick = { downloadServiceDocumentBlob(state.blobUrl, state.fileName) }
                )
                LumaticaButton(
                    label = "Schließen",
                    variant = LumaticaButtonVariant.Action,
                    heightPx = 38,
                    onClick = { ServiceDocumentViewerService.close() }
                )
            }
        }
        if (state.blobUrl.isNotBlank()) {
            PdfDocumentPreview(state.blobUrl, state.title)
        } else {
            Div({
                style {
                    flex(1)
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    justifyContent(JustifyContent.Center)
                    borderRadius(8.px)
                    backgroundColor(Color.white)
                    color(Color("#20242a"))
                }
            }) { Text("Dokument wird live gerendert …") }
        }
    }

    if (snapshot != null && state.blobUrl.isBlank()) {
        Div({
            style {
                position(Position.Fixed)
                left((-10000).px)
                top(0.px)
                width(794.px)
                property("pointer-events", "none")
                property("opacity", "0")
                property("z-index", "-1")
                backgroundColor(Color.white)
            }
        }) {
            RenderYardLetter(
                rootElementId = SERVICE_DOCUMENT_SNAPSHOT_RENDER_ROOT,
                layoutKey = snapshot.layoutKey,
                auftrag = snapshot.auftrag,
                vorgangslog = snapshot.vorgangslog,
                unternehmen = snapshot.unternehmen,
                logoSrc = snapshot.logoSrc,
                kontrollDefinitionen = snapshot.kontrollDefinitionen,
                webKontrolle = snapshot.webKontrolle,
                ausstellerSignatur = snapshot.ausstellerSignatur,
                ausstellerName = snapshot.ausstellerName,
                fahrerSignatur = snapshot.fahrerSignatur,
                fahrerName = snapshot.fahrerName
            )
        }
    }
}

private fun downloadServiceDocumentBlob(blobUrl: String, fileName: String) {
    if (blobUrl.isBlank()) return
    val anchor = document.createElement("a") as HTMLAnchorElement
    anchor.href = blobUrl
    anchor.download = fileName.ifBlank { "dokument.pdf" }
    document.body?.appendChild(anchor)
    anchor.click()
    anchor.remove()
}
